import Client from "./client";

export const Whoami = (accessToken) => {
    return new Promise((resolve, reject) => {
        Client.get('/api/auth/whoami', {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data)
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const Refresh = () => {
    return new Promise((resolve, reject) => {
        // No body/header needed - the httpOnly refreshToken cookie rides
        // along automatically (Client has withCredentials: true).
        Client.post('/api/auth/refresh').then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// planTier/billingInterval carry the plan picked on the pricing page. Left
// off (or STARTER) the account just starts on the free tier; a paid tier
// starts its free trial instead - no payment method, nothing at Stripe until
// the trial ends.
//
// planCode names the exact plan version whose price the form displayed. If
// that price changes between the page loading and the form being submitted,
// the API honors the figure the visitor was actually shown.
export const SignUp = ({name, email, password, confirmPassword, planTier, billingInterval, planCode, acceptedTerms}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/signup', {name, email, password, confirmPassword, planTier, billingInterval, planCode, acceptedTerms}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

export const Login = ({email, password}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/login', {email, password}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            // A PENDING account's 403 body still carries a usable accessToken
            // (see api/routes/auth.js) - forward the whole payload so the
            // caller can act on it rather than treating it as a plain failure.
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

// Signs in as the shared sandbox account, no credentials needed - see
// api/routes/auth.js's POST /auth/sandbox. Resolves {accessToken, user,
// project}, the same {accessToken, user} shape Login above does plus which
// project to enter. Rejects (unlike GetSandboxStatus in ./Sandbox, which
// never does) - a caller only reaches this by acting on a button
// useSandboxStatus already said was available, so a failure here is a real
// error worth surfacing, not an ordinary "not configured" state.
export const SandboxLogin = () => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/sandbox').then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

// Whether this deployment has Google sign-in configured, and the OAuth
// client id to initialize Google Identity Services with. Served by the API
// (rather than baked into the frontend build) so the client id lives in the
// same one place as the rest of the API's configuration. `clientId` is null
// when Google sign-in isn't set up.
export const GoogleConfig = () => {
    return new Promise((resolve, reject) => {
        Client.get('/api/auth/google/config').then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// Exchanges the ID token Google hands the browser for a session here,
// creating the local account from the Google profile on first sign-in.
export const GoogleLogin = ({credential, planTier, billingInterval, planCode, acceptedTerms}) => {
    return new Promise((resolve, reject) => {
        // The plan only applies when this turns out to be a new account -
        // signing in to an existing one keeps the plan it already has.
        Client.post('/api/auth/google', {credential, planTier, billingInterval, planCode, acceptedTerms}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

export const Logout = () => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/logout').then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// Asks for the confirmation email to be sent again, with a fresh code. The
// API throttles this per user (429 with a `retryAfter` in seconds) - it's
// the same window that limits confirmation attempts themselves.
export const ResendConfirmation = ({accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/confirm/resend', {}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

// Redeems the signed token from the "Confirm and sign in" link in the
// confirmation email: it confirms the account and starts a session on its
// own, so there's no access token to send along with it - the token is the
// credential here (and the API refuses it outright if it doesn't carry our
// signature).
export const ConfirmWithLink = ({token}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/confirm/link', {token}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

export const ConfirmAccount = ({code, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/confirm', {code}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

// Asks for a password-reset link to be emailed. Deliberately says the same
// thing whatever happened on the other end - whether the address has an
// account, and whether it has already had its two links for the hour, are
// not things this endpoint will tell the browser (see api/routes/auth.js).
// A 400 here only ever means the address wasn't a valid one.
export const RequestPasswordReset = ({email}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/password/forgot', {email}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}

// Checks the token from the emailed link before showing the form, without
// spending it - so an expired or already-used link says so up front rather
// than after the user has chosen a password.
export const VerifyPasswordResetToken = ({token}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/password/reset/verify', {token}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "That password reset link is not valid."});
        })
    })
}

// Redeems the link and sets the new password. No access token rides along -
// the signed, single-use token from the email is the credential here. It
// deliberately doesn't start a session: the user signs in with the new
// password straight afterwards.
export const ResetPassword = ({token, password, confirmPassword}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/auth/password/reset', {token, password, confirmPassword}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || {error: "Something went wrong. Please try again."});
        })
    })
}
